"use client";

import React, { useState, useRef, useEffect } from "react";
import { format, isAfter, isBefore } from "date-fns";
import { fr } from "date-fns/locale";
import { FiCalendar, FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface DateRangePickerProps {
  dateRange: { from: Date | null; to: Date | null } | null;
  onChange: (range: { from: Date | null; to: Date | null } | null) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateRange,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState<{
    from: Date | null;
    to: Date | null;
  } | null>(dateRange);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Reset temp range when dateRange changes
  useEffect(() => {
    setTempRange(dateRange);
  }, [dateRange]);

  const handleSelect = (date: Date) => {
    setTempRange((prev) => {
      if (!prev || !prev.from) return { from: date, to: null };
      if (!prev.to && isAfter(date, prev.from))
        return { from: prev.from, to: date };
      return { from: date, to: null };
    });
  };

  const handleApply = () => {
    onChange(tempRange);
    setIsOpen(false);
  };

  const handleClear = () => {
    setTempRange(null);
    onChange(null);
    setIsOpen(false);
  };

  const formatDateRange = () => {
    if (!dateRange) return "Sélectionner une période";
    if (dateRange.from && dateRange.to) {
      return `${format(dateRange.from, "dd/MM/yyyy", {
        locale: fr,
      })} - ${format(dateRange.to, "dd/MM/yyyy", { locale: fr })}`;
    }
    if (dateRange.from) {
      return `Depuis ${format(dateRange.from, "dd/MM/yyyy", { locale: fr })}`;
    }
    if (dateRange.to) {
      return `Jusqu'au ${format(dateRange.to, "dd/MM/yyyy", { locale: fr })}`;
    }
    return "Sélectionner une période";
  };

  const getDaysInMonth = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const isDateInRange = (date: Date) => {
    if (!tempRange || !tempRange.from) return false;

    const start = tempRange.from;
    const end = tempRange.to;

    if (!end) return isSameDay(date, start);

    return (
      (isAfter(date, start) || isSameDay(date, start)) &&
      (isBefore(date, end) || isSameDay(date, end))
    );
  };

  const isSameDay = (d1: Date, d2: Date) => {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(year, month);

    const daysOfWeek = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

    // Adjust for Sunday being 0 in JavaScript but we want Monday as first day
    const adjustedStartingDay =
      startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    const days = [];

    // Add empty cells for days before the 1st of the month
    for (let i = 0; i < adjustedStartingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-9 w-9"></div>);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const isSelected = isDateInRange(date);
      const isStart = tempRange?.from && isSameDay(date, tempRange.from);
      const isEnd = tempRange?.to && isSameDay(date, tempRange.to);

      days.push(
        <button
          key={`day-${i}`}
          className={`
            flex h-9 w-9 items-center justify-center rounded-md text-sm
            ${
              isSelected
                ? "bg-blue-100 dark:bg-blue-900/30"
                : "hover:bg-gray-100 dark:hover:bg-gray-800"
            }
            ${isStart ? "bg-blue-500 text-white hover:bg-blue-600" : ""}
            ${isEnd ? "bg-blue-500 text-white hover:bg-blue-600" : ""}
          `}
          onClick={() => handleSelect(date)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="calendar">
        <div className="mb-4 flex items-center justify-between">
          <button
            className="rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={prevMonth}
          >
            <FiChevronLeft className="h-5 w-5" />
          </button>
          <div className="font-medium">
            {format(currentMonth, "MMMM yyyy", { locale: fr })}
          </div>
          <button
            className="rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={nextMonth}
          >
            <FiChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium text-gray-500"
            >
              {day}
            </div>
          ))}
          {days}
        </div>
      </div>
    );
  };

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:border-navy-700 dark:bg-navy-900 dark:text-white/70"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <FiCalendar className="h-3.5 w-3.5" />
        <span className="whitespace-nowrap">{formatDateRange()}</span>
      </button>

      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute left-0 z-[999] mt-2 w-auto min-w-[300px] overflow-hidden rounded-md border border-gray-200 bg-white p-4 shadow-lg dark:border-navy-700 dark:bg-navy-800"
        >
          {renderCalendar()}

          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-navy-700">
            <button
              className="rounded-md border border-gray-200 px-3 py-1 text-sm dark:border-navy-700"
              onClick={handleClear}
            >
              Effacer
            </button>
            <button
              className="rounded-md bg-blue-500 px-3 py-1 text-sm text-white hover:bg-blue-600"
              onClick={handleApply}
            >
              Appliquer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
